import { useMutation, useQueryClient } from "@tanstack/react-query";
import { orderHistoryClaimApi } from "@/features/my-order/features/order-history/features/claim/api";
import useIdentity from "@/features/ui/hooks/use-identity";
import { companyTypeRequestFormatConverter } from "@/utils/converter";
import { PostClaimRequest } from "@/features/my-order/features/order-history/features/claim/types";
import useOrderHistoryDetail from "@/features/my-order/features/order-history/hooks/useOrderHistoryDetail";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { useClaimModalStore } from "@/features/my-order/features/order-history/stores/useClaimModalStore";

const usePostClaims = () => {
  const identity = useIdentity();
  const queryClient = useQueryClient();
  const { orderSheetSetId } = useOrderHistoryDetail();
  const { info } = useAppToasts();
  const { tSentence } = useI18nHelper();
  const { onClose } = useClaimModalStore();

  const companyType =
    companyTypeRequestFormatConverter[identity?.company?.type ?? "BUYER"];
  const companyId = identity?.company?.id;

  const { mutate: postClaims, isPending } = useMutation({
    mutationKey: ["order-history", "claim", orderSheetSetId],
    mutationFn: (params: PostClaimRequest) => {
      if (!orderSheetSetId || !companyType || !companyId) {
        return Promise.reject(new Error("Missing required parameters"));
      }
      return orderHistoryClaimApi.postClaims(
        companyType,
        companyId,
        orderSheetSetId,
        params
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "order-history",
          "claimable-products",
          companyType,
          companyId,
          orderSheetSetId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "order-history",
          "claim-list",
          companyType,
          companyId,
          orderSheetSetId,
        ],
      });

      onClose();
      info(tSentence("order_history:claim.toast_success_description"));
    },
  });

  return {
    postClaims,
    isPending,
  };
};

export default usePostClaims;
